import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColorPicker, Popover } from "@mantine/core";
import { ColorCircle } from "components/UI/ColorCircle";
import { TRACK_COLORS } from "config/constants";
import { Routing } from "config/enums/routings";
import { COLOR_SWATCHES } from "config/mantine";
import { useUserTracks } from "hooks/useUserTracks";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { changeTrackColor } from "stores/routingStore/actions";
import { useRouting } from "stores/routingStore/RoutingContext";
import { showErrorNotification } from "utils/notifications/customNotifications";

export const CircleColorPicker = () => {
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const {
    dispatch,
    state: { trackColor, routing },
  } = useRouting();
  const { selectedTrack, updateTrack } = useUserTracks();
  const { t } = useTranslation();
  const selectedTrackRef = useRef(selectedTrack);

  useEffect(() => {
    selectedTrackRef.current = selectedTrack;
  }, [selectedTrack]);

  const updateTrackColor = useCallback(
    (color: string) => {
      if (selectedTrackRef.current?.id) {
        updateTrack(selectedTrackRef.current?.id, {
          color: color ?? undefined,
        }).catch(() => {
          showErrorNotification({
            title: t("generic.error_title"),
            message: t("error_modal.generic_text"),
          });
        });
      }
    },
    [t, updateTrack]
  );

  // Picking a color rerenders this component, so the debounced function is created only once
  // (a new one on every render wouldn't debounce anything) and calls the current updateTrackColor
  const updateTrackColorRef = useRef(updateTrackColor);
  useEffect(() => {
    updateTrackColorRef.current = updateTrackColor;
  }, [updateTrackColor]);
  const debouncedUpdateTrack = useMemo(
    () => debounce((color: string) => updateTrackColorRef.current(color), 1000),
    []
  );

  const handleColorChange = (color: string) => {
    dispatch(changeTrackColor(color), false);
    debouncedUpdateTrack(color);
  };

  const defaultColorCircleColor =
    routing === Routing.none ? TRACK_COLORS.nonRoutable : TRACK_COLORS.routable;

  return (
    <Popover
      opened={isColorPickerOpen}
      onChange={setIsColorPickerOpen}
      position="bottom-end"
      closeOnClickOutside
    >
      <Popover.Target>
        <ColorCircle
          color={trackColor || defaultColorCircleColor}
          onClick={() => setIsColorPickerOpen((prev) => !prev)}
        />
      </Popover.Target>
      <Popover.Dropdown>
        <ColorPicker
          format="hex"
          swatches={COLOR_SWATCHES}
          swatchesPerRow={6}
          onChange={handleColorChange}
          value={trackColor || TRACK_COLORS.routable}
        />
      </Popover.Dropdown>
    </Popover>
  );
};
